import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import {
  TWashingAppDetailsWithAdditionalInfo,
  TBctxItemWithoutSize,
  TBgDBLockedBy,
  TBgDBLockResult,
  TBulkCountAppBackgroundDB,
  TBulkCountAppDBStore,
  TBulkCountAppEventWithStatus,
  TBulkCountAppUserActualsDB,
  TBulkCountSyncEventsDTO,
  TBulkCountUserAction,
  TBulkGradingPageSizeItem,
  TBulkWashingEventType,
  TCommonLockedBy,
  TEventStationDetails,
  TREQUEST_BulkSaveWashReceiveRequest,
  TREQUEST_BulkSaveWashSendRequest,
  TUserDBLockedBy,
  TUserDBLockResult,
  TVendorDTO,
  TBulkCountPendingTransactionWithBctxList,
  TRESPONSE_WashingReceiveBctxDataResponse,
  TBCTXKeysWithoutSize,
  TBulkCountClientDBInitInfo,
  TRemoteDeploymentCheck,
  TRemoteDeploymentCheckWithEventsSyncResult,
  TWashingUndoTransactionDetail,
} from '@monorepo/types';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import {
  getAllVendors,
  getBctxItemWithoutSizeAndBctxKey,
  getBctxWithSizeList,
  getBgDBLockDetailsAndNextCacheResetTime,
  getClientDBAndOnlineStatusAndEventsBatchCountAndCurrentLineAndShift,
  getPendingTransactionsWithBctxList,
  getSizeList,
  getTotalQty,
  getTransactions,
  getTransactionsByType,
  getUserDBAndBgDBLockDetails,
  getUserDBLockDetailsAndNextCacheResetTime,
  selectUserDB,
  getSelectedStationIdAndShiftId,
  getSelectedStationAndShiftDTO,
} from './bulk-count-app-db.query';
import {
  AppMasterDBFacade,
  getBctxItemsOrderWithoutSize,
  getBctxKeysToDisplayMap,
  getBulkCountAppInitialState,
  getBusinessContextDataPayload,
  getConfigurations,
  getIsVendorEnabled,
  PersistentStorageService,
} from '@monorepo/core-data';
import {
  BCTXComputationHelper,
  BulkCountAppComputationHelper,
  BulkCountAppEventUtil,
  InputAppAPIHelperUtil,
} from '@monorepo/utils';
import {
  AcquireBgDBLockAndThenSyncEvents,
  AcquireLockAndThenInitializeAllDBs,
  AcquireUserAndBgDBLockAndThenReconcileForWashing,
  AcquireUserDBLockAndRemoveTransaction,
  AcquireUserDBLockAndSaveEvent,
  AcquireUserDBLockAndSavePendingWork,
  AcquireUserDBLockAndSyncWashingInStyleInfo,
  AcquireUserDBLockAndThenSyncReceiveCount,
  AcquireUserDBLockAndThenSyncTransactions,
  AcquireUserDBLockAndUndoWashingSendEvent,
  AcquireUserDBLockAndUndoWashingReceiveEvent,
  AcquireUserDBLockAndUpdatePendingWork,
  InitVendorDB,
  SyncVendors,
  UpdateBgDBLockDetails,
  UpdateBulkCountAppBackgroundDB,
  UpdateLockDetails,
} from './bulk-count-app-db.action';
import { from, Observable, of, Subject } from 'rxjs';
import produce from 'immer';
import { BulkCountAppDataService, HttpResponseException } from '@monorepo/services';
import { AlertController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
@Injectable({
  providedIn: 'root',
})
export class BulkCountAppFacade {
  private initBulkCountUserDBAndBgDBSubscription = new Subject<TBulkCountClientDBInitInfo>();
  private appUpdateSubscription = new Subject<TRemoteDeploymentCheckWithEventsSyncResult>();
  constructor(
    private platform: Platform,
    private store: Store<TBulkCountAppDBStore>,
    private persistanceStorageService: PersistentStorageService,
    private appMasterDBFacade: AppMasterDBFacade,
    private bulkCountAppDataService: BulkCountAppDataService,
    private inputAppAPIHelperUtil: InputAppAPIHelperUtil,
    private alertCtrl: AlertController,
    private translate: TranslateService,
  ) {}

  getAppUpdateSubscription() {
    return this.appUpdateSubscription.asObservable();
  }

  emitAppUpdateSubscription(remoteDeploymentCheckWithSyncResult: TRemoteDeploymentCheckWithEventsSyncResult) {
    this.appUpdateSubscription.next(remoteDeploymentCheckWithSyncResult);
  }

  getInitBulkCountUserDBAndBgDBSubscription() {
    return this.initBulkCountUserDBAndBgDBSubscription.asObservable();
  }

  emitInitBulkCountUserDBAndBgDBSubscription(dto: TBulkCountClientDBInitInfo) {
    this.initBulkCountUserDBAndBgDBSubscription.next(dto);
  }

  syncVendors() {
    return this.store.dispatch(new SyncVendors(true));
  }

  getAllVendors() {
    return this.store.pipe(select(getAllVendors));
  }

  getConfigurations() {
    return this.store.pipe(select(getConfigurations));
  }

  getClientDBAndOnlineStatusAndEventsBatchCountAndCurrentLineAndShift() {
    return this.store.pipe(select(getClientDBAndOnlineStatusAndEventsBatchCountAndCurrentLineAndShift));
  }

  getSelectedStationIdAndShiftId() {
    return this.store.pipe(select(getSelectedStationIdAndShiftId));
  }

  getUserDBLockDetailsAndNextCacheResetTime() {
    return this.store.pipe(select(getUserDBLockDetailsAndNextCacheResetTime));
  }

  getBgDBLockDetailsAndNextCacheResetTime() {
    return this.store.pipe(select(getBgDBLockDetailsAndNextCacheResetTime));
  }

  getClientDB() {
    return this.store.pipe(select(selectUserDB));
  }

  isUserAndBgDBsUnlocked() {
    return this.store.pipe(select(getUserDBAndBgDBLockDetails)).pipe(
      take(1),
      map(({ userDBLockDetails, bgDBLockDetails }) => !userDBLockDetails.isLocked && !bgDBLockDetails.isLocked),
    );
  }

  acquireUserDBLock(lockFor: TUserDBLockedBy): Observable<TUserDBLockResult> {
    return this.getUserDBLockDetailsAndNextCacheResetTime().pipe(
      take(1),
      map(({ lockDetails, nextCacheResetTimestamp }) => {
        if (lockDetails.isLocked) {
          return {
            status: 'FAILURE',
            lockedBy: lockDetails.lockedBy,
            message: `${this.translate.instant('input_app_image_db_effects.please_wait')} (${lockDetails.lockedBy})`,
          };
        } else if (lockFor !== 'INIT_PENDING' && nextCacheResetTimestamp < new Date().getTime()) {
          return {
            status: 'FAILURE',
            lockedBy: lockDetails.lockedBy,
            message: this.translate.instant('input_app_image_db_effects.factoryEndTime'),
          };
        } else {
          this.store.dispatch(
            new UpdateLockDetails(
              produce(lockDetails, (draft) => {
                draft.isLocked = true;
                draft.lockedBy = lockFor;
              }),
            ),
          );
          return {
            status: 'SUCCESS',
            lockedBy: lockFor,
          };
        }
      }),
    );
  }

  acquireBgDBLock(lockFor: TBgDBLockedBy): Observable<TBgDBLockResult> {
    return this.getBgDBLockDetailsAndNextCacheResetTime().pipe(
      take(1),
      map(({ lockDetails, nextCacheResetTimestamp }) => {
        if (lockDetails.isLocked) {
          return {
            status: 'FAILURE',
            lockedBy: lockDetails.lockedBy,
            message: `${this.translate.instant('input_app_image_db_effects.please_wait')} (${lockDetails.lockedBy})`,
          };
        } else if (lockFor !== 'INIT_PENDING' && nextCacheResetTimestamp < new Date().getTime()) {
          return {
            status: 'FAILURE',
            lockedBy: lockDetails.lockedBy,
            message: this.translate.instant('input_app_image_db_effects.factoryEndTime'),
          };
        } else if (lockFor === 'SYNC_EVENTS') {
          const metadata = lockDetails.metadata;
          const eventAdditionMetadata = metadata[lockFor];
          const currentTime = new Date().getTime();
          if (eventAdditionMetadata.nextLockUpto && eventAdditionMetadata.nextLockUpto >= currentTime) {
            return {
              status: 'FAILURE',
              lockedBy: 'NONE',
              message:
                this.translate.instant('input_app_image_db_effects.previousBatchSyncing.header') +
                this.translate.instant('input_app_image_db_effects.previousBatchSyncing.message'),
            };
          } else {
            this.store.dispatch(
              new UpdateBgDBLockDetails(
                produce(lockDetails, (draft) => {
                  draft.isLocked = true;
                  draft.lockedBy = 'SYNC_EVENTS';
                  draft.metadata['SYNC_EVENTS'] = {};
                }),
              ),
            );
            return {
              status: 'SUCCESS',
              lockedBy: 'SYNC_EVENTS',
            };
          }
        } else {
          this.store.dispatch(
            new UpdateBgDBLockDetails(
              produce(lockDetails, (draft) => {
                draft.isLocked = true;
                draft.lockedBy = lockFor;
              }),
            ),
          );
          return {
            status: 'SUCCESS',
            lockedBy: lockFor,
          };
        }
      }),
    );
  }

  acquireUserAndBgDBLock(lockFor: TCommonLockedBy) {
    return this.acquireBgDBLock(lockFor).pipe(
      take(1),
      switchMap((bgLockResult) => {
        return this.acquireUserDBLock(lockFor).pipe(
          take(1),
          map((userDBLockResult) => {
            return [bgLockResult, userDBLockResult] as const;
          }),
        );
      }),
    );
  }

  releaseUserDBLock() {
    this.getUserDBLockDetailsAndNextCacheResetTime()
      .pipe(take(1))
      .subscribe(({ lockDetails }) => {
        this.store.dispatch(new UpdateLockDetails(BCTXComputationHelper.releaseUserDBLock(lockDetails)));
      });
  }

  releaseBgDBLock() {
    this.getBgDBLockDetailsAndNextCacheResetTime()
      .pipe(take(1))
      .subscribe(({ lockDetails }) => {
        this.store.dispatch(new UpdateBgDBLockDetails(BCTXComputationHelper.releaseBgLock(lockDetails)));
      });
  }

  backgroundSyncEvents(eventType: TBulkWashingEventType) {
    this.getClientDBAndOnlineStatusAndEventsBatchCountAndCurrentLineAndShift()
      .pipe(take(1))
      .subscribe(({ isOnline, userDB, bgDB, batchCount }) => {
        if (isOnline && BulkCountAppEventUtil.hasAnyPendingEvents(userDB, bgDB)) {
          this.store.dispatch(new AcquireBgDBLockAndThenSyncEvents(false, batchCount, eventType));
        }
      });
  }

  isWeb() {
    return !(this.platform.is('android') || this.platform.is('ios'));
  }

  addMissingEventsStatusInBgDB(
    userDBEvents: TBulkCountAppUserActualsDB['dailyCache']['events']['list'],
    bgDB: TBulkCountAppBackgroundDB,
  ) {
    return this.getConfigurations().pipe(
      take(1),
      switchMap(({ retryCount }) => {
        const eventsWithStatus = BulkCountAppComputationHelper.getEventsWithStatus(
          userDBEvents,
          bgDB.dailyCache.events.list,
          retryCount,
        );
        bgDB = produce(bgDB, (draft) => {
          draft.dailyCache.events.list = eventsWithStatus.map((item) => item.backgroundMetadata);
        });
        return from(this.persistanceStorageService.set('[APP][INPUT_APP_BULK_COUNT_BACKGROUND_DB]', bgDB)).pipe(
          take(1),
          map(() => {
            this.store.dispatch(new UpdateBulkCountAppBackgroundDB(bgDB));
            return [bgDB, eventsWithStatus] as const;
          }),
        );
      }),
    );
  }

  syncPendingEventWithBatchCount(
    bulkCountSyncEventsDTO: TBulkCountSyncEventsDTO,
    batchCount: number,
    eventType: TBulkCountAppEventWithStatus['event']['type'],
  ): Observable<TBulkCountSyncEventsDTO> {
    let { events, failedEventsIndices } = bulkCountSyncEventsDTO;
    let lastSyncedIndex = BulkCountAppEventUtil.getLastSyncedIndex(events);
    if (lastSyncedIndex === events.length - 1) {
      return of({
        events,
        failedEventsIndices,
        lastBatchSyncStatus: 'ALL_SYNCED',
      });
    } else {
      const toBeSyncedEvents = events.slice(lastSyncedIndex + 1, lastSyncedIndex + 1 + batchCount);
      if (eventType === 'SEND') {
        return this.getBulkCountAppSendPayload(toBeSyncedEvents).pipe(
          take(1),
          switchMap((payload) =>
            this.bulkCountAppDataService.saveBulkSendEvents(payload).pipe(
              take(1),
              switchMap((res) => {
                const [updateEvents, updatedFailedIndices] =
                  BulkCountAppComputationHelper.getUpdatedEventStatusAfterSaveAndFailedEventIndices(
                    lastSyncedIndex + 1,
                    events,
                    failedEventsIndices,
                    res,
                  );

                return this.syncPendingEventWithBatchCount(
                  {
                    events: updateEvents,
                    failedEventsIndices: updatedFailedIndices,
                    lastBatchSyncStatus: 'YET_TO_BE_SYNCED',
                  },
                  batchCount,
                  eventType,
                );
              }),
              catchError((error: HttpResponseException) => {
                return BulkCountAppComputationHelper.getBulkCountSyncEventsDTOAfterServerError(
                  error,
                  lastSyncedIndex,
                  batchCount,
                  failedEventsIndices,
                  events,
                  toBeSyncedEvents,
                );
              }),
            ),
          ),
        );
      } else {
        return this.getBulkCountAppReceivePayload(toBeSyncedEvents).pipe(
          take(1),
          switchMap((payload) =>
            this.bulkCountAppDataService.saveBulkReceiveEvents(payload).pipe(
              take(1),
              switchMap((res) => {
                const [updateEvents, updatedFailedIndices] =
                  BulkCountAppComputationHelper.getUpdatedEventStatusAfterSaveAndFailedEventIndices(
                    lastSyncedIndex + 1,
                    events,
                    failedEventsIndices,
                    res,
                  );

                return this.syncPendingEventWithBatchCount(
                  {
                    events: updateEvents,
                    failedEventsIndices: updatedFailedIndices,
                    lastBatchSyncStatus: 'YET_TO_BE_SYNCED',
                  },
                  batchCount,
                  eventType,
                );
              }),
              catchError((error: HttpResponseException) => {
                return BulkCountAppComputationHelper.getBulkCountSyncEventsDTOAfterServerError(
                  error,
                  lastSyncedIndex,
                  batchCount,
                  failedEventsIndices,
                  events,
                  toBeSyncedEvents,
                );
              }),
            ),
          ),
        );
      }
    }
  }

  getBctxKeysToDisplayMap() {
    return this.store.pipe(select(getBctxKeysToDisplayMap));
  }

  showFailedEvents(sizeAndCountList: TBulkCountAppEventWithStatus[]) {
    this.getBctxKeysToDisplayMap()
      .pipe(take(1))
      .subscribe((bctxKeysToDisplayMap) => {
        return from(
          this.inputAppAPIHelperUtil.showWipValidationErrorForBulkCountApp(sizeAndCountList, bctxKeysToDisplayMap),
        );
      });
  }

  syncAtLeastOnePendingBulkEventWithBatchCountWrapper(
    events: TBulkCountAppEventWithStatus[],
    batchCount: number,
    eventType: TBulkCountAppEventWithStatus['event']['type'],
  ) {
    return this.syncPendingEventWithBatchCount(
      { events, lastBatchSyncStatus: 'YET_TO_BE_SYNCED', failedEventsIndices: [] },
      batchCount,
      eventType,
    );
  }

  getAppDetailsWithAdditionalInfo(
    currentAppDetailsWithAdditionalInfo: TWashingAppDetailsWithAdditionalInfo,
    eventStationDetails: TEventStationDetails,
  ) {
    const { subject, shift } = eventStationDetails;
    const appDetails: TWashingAppDetailsWithAdditionalInfo = {
      ...currentAppDetailsWithAdditionalInfo,
      shift: shift.id + '_D_' + BCTXComputationHelper.dashFormateDate(),
      subject: subject.subject,
      dnm: subject.name,
      dnr: subject.gatev,
      snm: subject.dis_name,
      dnrk: subject.gatek,
      shiftDn: shift.name,
    };
    return appDetails;
  }

  getBulkCountAppSendPayload(events: TBulkCountAppEventWithStatus[]) {
    return this.appMasterDBFacade.getWashingAppDetailsWithAdditionalInfo().pipe(
      take(1),
      switchMap((currentAppDetailsWithAdditionalInfo) => {
        const requests: TREQUEST_BulkSaveWashSendRequest['washSendRequests'] = events.map(
          (bulkCountAppEventWithStatus) => {
            const request: TREQUEST_BulkSaveWashSendRequest['washSendRequests'][number] = {
              appDetails: this.getAppDetailsWithAdditionalInfo(
                currentAppDetailsWithAdditionalInfo,
                bulkCountAppEventWithStatus.event.metaData.stationDetails,
              ),
              vendorId: bulkCountAppEventWithStatus.event.metaData.vendorId,
              bctxWithoutSize: bulkCountAppEventWithStatus.event.metaData.bctxWithoutSize,
              sizeQuantities: bulkCountAppEventWithStatus.event.sizeQuantities,
              eventTime: bulkCountAppEventWithStatus.event.metaData.eventTime,
            };
            return request;
          },
        );
        const bulkSaveWashSendPayload: TREQUEST_BulkSaveWashSendRequest = {
          factoryId: requests[0].appDetails.factoryId,
          washSendRequests: requests,
        };
        return of(bulkSaveWashSendPayload);
      }),
    );
  }

  getBulkCountAppReceivePayload(events: TBulkCountAppEventWithStatus[]) {
    return this.appMasterDBFacade.getWashingAppDetailsWithAdditionalInfo().pipe(
      take(1),
      switchMap((currentAppDetailsWithAdditionalInfo) => {
        const requests: TREQUEST_BulkSaveWashReceiveRequest['washReceiveRequests'] = events.map(
          (bulkCountAppEventWithStatus) => {
            const request: TREQUEST_BulkSaveWashReceiveRequest['washReceiveRequests'][number] = {
              appDetails: this.getAppDetailsWithAdditionalInfo(
                currentAppDetailsWithAdditionalInfo,
                bulkCountAppEventWithStatus.event.metaData.stationDetails,
              ),
              vendorId: bulkCountAppEventWithStatus.event.metaData.vendorId,
              bctxWithoutSize: bulkCountAppEventWithStatus.event.metaData.bctxWithoutSize.filter((x) => x.v !== null),
              sizeQuantities: bulkCountAppEventWithStatus.event.sizeQuantities,
              eventTime: bulkCountAppEventWithStatus.event.metaData.eventTime,
            };
            return request;
          },
        );
        const bulkSaveWashReceivePayload: TREQUEST_BulkSaveWashReceiveRequest = {
          factoryId: requests[0].appDetails.factoryId,
          washReceiveRequests: requests,
        };
        return of(bulkSaveWashReceivePayload);
      }),
    );
  }

  reconcileUserAndBgEventsDB(
    userDB: TBulkCountAppUserActualsDB,
    bgDB: TBulkCountAppBackgroundDB,
    nextCacheResetTimestamp: number,
    eventType: TBulkWashingEventType,
  ) {
    return this.getConfigurations().pipe(
      take(1),
      map(({ retryCount, delayedEventsSLAInDays, cacheResetTimestampInMilliSeconds }) => {
        const events = BulkCountAppComputationHelper.getEventsWithStatus(
          userDB.dailyCache.events.list,
          bgDB.dailyCache.events.list,
          retryCount,
        );
        const mergedLineMap = BulkCountAppComputationHelper.mergeUserLineMapWithBgLineMap(
          userDB.dailyCache.lineMap,
          bgDB.dailyCache.lineMap,
        );
        const initialState = getBulkCountAppInitialState(nextCacheResetTimestamp);
        if (userDB.dailyCache.nextCacheResetTimestamp === nextCacheResetTimestamp) {
          userDB = produce(userDB, (draft) => {
            draft.dailyCache.lineMap = mergedLineMap;
            draft.dailyCache.totalCount += bgDB.dailyCache.totalCount;
          });
        } else {
          userDB = produce(initialState.userDB, (draft) => {
            draft.persistanceCache = userDB.persistanceCache;
          });
          const updatedPendingTransactions = BulkCountAppEventUtil.getNonDelayedTransactions(
            delayedEventsSLAInDays,
            userDB.persistanceCache.pendingTransactions,
            cacheResetTimestampInMilliSeconds,
          );
          const updatedLineMap = BulkCountAppComputationHelper.removeAllBctxApartFromPendingTransactionsAndZeroWip(
            mergedLineMap,
            updatedPendingTransactions,
            eventType,
          );
          userDB = produce(userDB, (draft) => {
            draft.persistanceCache.pendingTransactions = updatedPendingTransactions;
            draft.dailyCache.lineMap = updatedLineMap;
          });
        }

        if (bgDB.dailyCache.nextCacheResetTimestamp === nextCacheResetTimestamp) {
          bgDB = produce(bgDB, (draft) => {
            draft.dailyCache.lineMap = {};
            draft.dailyCache.totalCount = 0;
            draft.dailyCache.events.list = events.map((event) => event.backgroundMetadata);
          });
        } else {
          bgDB = produce(initialState.bgDB, (draft) => {
            draft.persistanceCache = bgDB.persistanceCache;
          });
          const updatedPastDaysEvents = BulkCountAppEventUtil.getNonDelayedEvents(
            delayedEventsSLAInDays,
            [...bgDB.persistanceCache.pastDaysList, ...events].filter((event) => {
              return event.backgroundMetadata.eventStatus === 'YET_TO_BE_SYNCED';
            }),
            cacheResetTimestampInMilliSeconds,
          );
          bgDB = produce(bgDB, (draft) => {
            draft.persistanceCache.pastDaysList = updatedPastDaysEvents;
          });
        }
        return [userDB, bgDB] as const;
      }),
    );
  }

  getFilteredVendorWiseBctxData(
    res: TRESPONSE_WashingReceiveBctxDataResponse,
    orderKeys: TBCTXKeysWithoutSize[],
    stationId: string,
    shiftId: string,
    currentDB: TBulkCountAppUserActualsDB,
  ): TRESPONSE_WashingReceiveBctxDataResponse {
    return {
      vendorsToBctxData: Object.entries(res.vendorsToBctxData).reduce((acc, [vendorId, vendorToBctxData]) => {
        vendorToBctxData.bctxData.forEach((bctxInfo) => {
          const orderedBctx = BulkCountAppComputationHelper.getOrderedBctx(bctxInfo.bctxWithoutSize, orderKeys);
          const bctxKeyAndVendorId = BulkCountAppComputationHelper.getUniqueBctxKeyAndVendorIdWithOrder(
            orderKeys,
            orderedBctx,
            parseInt(vendorId),
          );
          const filteredsizeData = bctxInfo.sizeCumulativeQuantities.filter((sizeInfo) => {
            let filter = false;
            if (
              currentDB.dailyCache.lineMap[stationId] &&
              currentDB.dailyCache.lineMap[stationId].shiftMap[shiftId] &&
              currentDB.dailyCache.lineMap[stationId].shiftMap[shiftId][bctxKeyAndVendorId] &&
              currentDB.dailyCache.lineMap[stationId].shiftMap[shiftId][bctxKeyAndVendorId].sizeMap[sizeInfo.size]
            ) {
              filter =
                currentDB.dailyCache.lineMap[stationId].shiftMap[shiftId][bctxKeyAndVendorId].sizeMap[sizeInfo.size]
                  .sentQty !== sizeInfo.cumulativeSentQuantity;
            }
            return sizeInfo.cumulativeSentQuantity !== sizeInfo.cumulativeReceivedQuantity || filter;
          });
          if (filteredsizeData.length) {
            if (!acc[vendorId])
              acc[vendorId] = {
                vendor: vendorToBctxData.vendor,
                bctxData: [],
              };
            acc[vendorId].bctxData.push({
              sizeCumulativeQuantities: filteredsizeData,
              bctxWithoutSize: bctxInfo.bctxWithoutSize,
            });
          }
        });
        return acc;
      }, {} as TRESPONSE_WashingReceiveBctxDataResponse['vendorsToBctxData']),
    };
  }

  initializeAllDBs(bulkWashingEventType: TBulkWashingEventType) {
    this.store.dispatch(new AcquireLockAndThenInitializeAllDBs(bulkWashingEventType));
  }

  initVendorDB() {
    this.store.dispatch(new InitVendorDB());
  }

  getBctxWithSizeList() {
    return this.store.pipe(select(getBctxWithSizeList));
  }

  pollForBgDBLock(chainAction: TBulkCountUserAction, isLoaderOpened = false, eventType: TBulkWashingEventType) {
    this.acquireUserDBLock('POLLING_FOR_BG_LOCK')
      .pipe(take(1))
      .subscribe(async (userDBLockDetails) => {
        if (userDBLockDetails.status === 'SUCCESS') {
          this.acquireBgDBLock('POLLING_FOR_BG_LOCK')
            .pipe(take(1))
            .subscribe(async (bgDBLockDetails) => {
              if (bgDBLockDetails.status === 'SUCCESS') {
                if (isLoaderOpened) this.inputAppAPIHelperUtil.dismissLoading();
                // both locks were just acquired here, proving no one else held them, so it's safe to
                // release and pass isBgDBUnlocked=true downstream instead of polling again
                this.releaseUserDBLock();
                this.releaseBgDBLock();
                if (chainAction.chainWithInitForLineChange) {
                  this.initLineChangeAfterSyncingPendingEvents(true, false, chainAction.chainWithInitForLineChange);
                } else if (chainAction.chainWithAppUpdate) {
                  this.forceSyncEventsAndUpdateApp(eventType, chainAction.chainWithAppUpdate, true);
                } else if (chainAction.chainWithUndoWashingEvent) {
                  this.forceSyncPendingEventsAndThenUndoCurrentEvent(
                    chainAction.chainWithUndoWashingEvent.eventType,
                    true,
                    false,
                    chainAction.chainWithUndoWashingEvent,
                  );
                }
              } else {
                if (!isLoaderOpened) await this.inputAppAPIHelperUtil.presentLoading('Evaluating pending events...');
                setTimeout(() => {
                  this.releaseUserDBLock();
                  this.pollForBgDBLock(chainAction, true, eventType);
                }, 2000);
              }
            });
        } else {
          if (isLoaderOpened) this.inputAppAPIHelperUtil.dismissLoading();
          this.inputAppAPIHelperUtil.showPopup('Error', userDBLockDetails.message);
        }
      });
  }

  async showWarningAndDeleteTransaction(details: TBulkCountPendingTransactionWithBctxList) {
    const alert = await this.alertCtrl.create({
      header: 'Warning',
      subHeader: 'Are you sure you want to delete the saved-transaction?',
      buttons: [
        {
          text: 'No',
          handler: () => {},
        },
        {
          text: 'Yes',
          handler: () => {
            this.store.dispatch(new AcquireUserDBLockAndRemoveTransaction(details));
          },
        },
      ],
    });
    await alert.present();
  }

  getBusinessContextDataPayload() {
    return this.store.pipe(select(getBusinessContextDataPayload));
  }

  getBctxItemsOrderWithoutSize() {
    return this.store.pipe(select(getBctxItemsOrderWithoutSize));
  }

  getSelectedStationAndShiftDTO() {
    return this.store.pipe(select(getSelectedStationAndShiftDTO));
  }

  initLineChangeAfterSyncingPendingEvents(
    isBgDBUnlocked: boolean,
    isReconcileCompleted: boolean,
    eventType: TBulkWashingEventType,
  ) {
    this.getClientDBAndOnlineStatusAndEventsBatchCountAndCurrentLineAndShift()
      .pipe(take(1))
      .subscribe(({ isOnline, batchCount, userDB, bgDB }) => {
        if (!isOnline)
          this.inputAppAPIHelperUtil.showPopup('Error', 'Not connected to internet, please check the connection');
        else if (!isBgDBUnlocked) {
          this.pollForBgDBLock({ chainWithInitForLineChange: eventType }, undefined, eventType);
        } else if (BulkCountAppEventUtil.hasAnyPendingEvents(userDB, bgDB)) {
          this.store.dispatch(
            new AcquireBgDBLockAndThenSyncEvents(true, batchCount, eventType, {
              chainWithInitForLineChange: eventType,
            }),
          );
        } else if (!isReconcileCompleted) {
          this.store.dispatch(
            new AcquireUserAndBgDBLockAndThenReconcileForWashing(
              true,
              { chainWithInitForLineChange: eventType },
              eventType,
            ),
          );
        } else {
          if (eventType === 'SEND') this.store.dispatch(new AcquireUserDBLockAndSyncWashingInStyleInfo());
          else this.store.dispatch(new AcquireUserDBLockAndThenSyncReceiveCount());
        }
      });
  }

  initSyncStyleInfoOrReceiveCount(eventType: TBulkWashingEventType) {
    if (eventType === 'SEND') this.store.dispatch(new AcquireUserDBLockAndSyncWashingInStyleInfo());
    else this.store.dispatch(new AcquireUserDBLockAndThenSyncReceiveCount());
  }

  getBctxItemWithoutSizeAndBctxKey(vendorId: TVendorDTO['vendorId'] | undefined, eventType: TBulkWashingEventType) {
    return this.store.pipe(select(getBctxItemWithoutSizeAndBctxKey(vendorId, eventType)));
  }

  sync(eventType: TBulkWashingEventType) {
    this.initLineChangeAfterSyncingPendingEvents(false, false, eventType);
  }

  getSizeList(
    eventType: TBulkWashingEventType,
    bctxList: TBctxItemWithoutSize[],
    vendorId: TVendorDTO['vendorId'] | undefined,
  ) {
    return this.store.pipe(select(getSizeList(eventType, bctxList, vendorId)));
  }

  getIsVendorEnabled() {
    return this.store.pipe(select(getIsVendorEnabled));
  }
  savePendingWork(
    eventType: TBulkWashingEventType,
    bctxList: TBctxItemWithoutSize[],
    sizeList: TBulkGradingPageSizeItem[],
    vendorId: TVendorDTO['vendorId'] | undefined,
  ) {
    return this.store.dispatch(new AcquireUserDBLockAndSavePendingWork(eventType, bctxList, sizeList, vendorId));
  }

  updatePendingWork(
    eventType: TBulkWashingEventType,
    bctxList: TBctxItemWithoutSize[],
    vendorId: TVendorDTO['vendorId'] | undefined,
    updatedSavedQty: Record<string, number>,
  ) {
    return this.store.dispatch(
      new AcquireUserDBLockAndUpdatePendingWork(eventType, bctxList, vendorId, updatedSavedQty),
    );
  }

  sendOrReceiveForProcess(
    eventType: TBulkWashingEventType,
    bctxList: TBctxItemWithoutSize[],
    sizeList: TBulkGradingPageSizeItem[],
    vendorId: TVendorDTO['vendorId'],
  ) {
    return this.store.dispatch(new AcquireUserDBLockAndSaveEvent(eventType, bctxList, sizeList, vendorId));
  }

  getTotalQty() {
    return this.store.pipe(select(getTotalQty));
  }

  loadSyncedTransactions(eventType: TBulkWashingEventType, startTime: number, endTime: number) {
    this.store.dispatch(new AcquireUserDBLockAndThenSyncTransactions(eventType, startTime, endTime));
  }

  getTransactions() {
    return this.store.pipe(select(getTransactions));
  }

  getTransactionsByType<T extends TBulkWashingEventType>(eventType: T) {
    return this.store.pipe(select(getTransactionsByType(eventType)));
  }

  getPendingTransactionsWithBctxList() {
    return this.store.pipe(select(getPendingTransactionsWithBctxList));
  }

  forceSyncEventsAndUpdateApp(eventType: TBulkWashingEventType, dto: TRemoteDeploymentCheck, isBgDBUnlocked: boolean) {
    this.getClientDBAndOnlineStatusAndEventsBatchCountAndCurrentLineAndShift()
      .pipe(take(1))
      .subscribe(({ isOnline, userDB, bgDB, batchCount }) => {
        if (!isBgDBUnlocked) {
          this.pollForBgDBLock(
            {
              chainWithAppUpdate: dto,
            },
            undefined,
            eventType,
          );
        } else if (!isOnline) {
          this.reconcileAndShowErrorForAppUpdates(
            {
              syncResult: {
                status: 'NO_INTERNET_FOR_SYNCING_PENDING_EVENTS',
              },
              remoteDeploymentcheck: dto,
            },
            eventType,
          );
        } else if (BulkCountAppEventUtil.hasAnyPendingEvents(userDB, bgDB)) {
          this.store.dispatch(
            new AcquireBgDBLockAndThenSyncEvents(true, batchCount, eventType, {
              chainWithAppUpdate: dto,
            }),
          );
        } else {
          this.reconcileAndShowErrorForAppUpdates(
            {
              syncResult: {
                status: 'ALL_SYNCED',
              },
              remoteDeploymentcheck: dto,
            },
            eventType,
          );
        }
      });
  }

  reconcileAndShowErrorForAppUpdates(
    dto: TRemoteDeploymentCheckWithEventsSyncResult,
    eventType: TBulkWashingEventType,
  ) {
    this.store.dispatch(
      new AcquireUserAndBgDBLockAndThenReconcileForWashing(
        true,
        {
          reconcileAndShowAppUpdateError: dto,
        },
        eventType,
      ),
    );
  }

  forceSyncPendingEventsAndThenUndoCurrentEvent(
    eventType: TBulkWashingEventType,
    isBgDBUnlocked: boolean,
    isReconcileCompleted: boolean,
    payload: TWashingUndoTransactionDetail & { startTime: number; endTime: number },
  ) {
    this.getClientDBAndOnlineStatusAndEventsBatchCountAndCurrentLineAndShift()
      .pipe(take(1))
      .subscribe(({ isOnline, batchCount, userDB, bgDB }) => {
        if (!isOnline)
          this.inputAppAPIHelperUtil.showPopup('Error', this.translate.instant('bulk_washing_undo.noInternet'));
        else if (!isBgDBUnlocked) {
          this.pollForBgDBLock({ chainWithUndoWashingEvent: { ...payload, eventType } }, undefined, eventType);
        } else if (BulkCountAppEventUtil.hasAnyPendingEvents(userDB, bgDB)) {
          this.store.dispatch(
            new AcquireBgDBLockAndThenSyncEvents(true, batchCount, eventType, {
              chainWithUndoWashingEvent: { ...payload, eventType },
            }),
          );
        } else if (!isReconcileCompleted) {
          this.store.dispatch(
            new AcquireUserAndBgDBLockAndThenReconcileForWashing(
              true,
              { chainWithUndoWashingEvent: { ...payload, eventType } },
              eventType,
            ),
          );
        } else if (eventType === 'SEND') {
          this.store.dispatch(
            new AcquireUserDBLockAndUndoWashingSendEvent(payload, payload.startTime, payload.endTime),
          );
        } else {
          this.store.dispatch(
            new AcquireUserDBLockAndUndoWashingReceiveEvent(payload, payload.startTime, payload.endTime),
          );
        }
      });
  }

  undoWashingEvent(payload: TWashingUndoTransactionDetail, startTime: number, endTime: number) {
    this.forceSyncPendingEventsAndThenUndoCurrentEvent('SEND', false, false, { ...payload, startTime, endTime });
  }

  undoReceiveWashingEvent(payload: TWashingUndoTransactionDetail, startTime: number, endTime: number) {
    this.forceSyncPendingEventsAndThenUndoCurrentEvent('RECEIVE', false, false, { ...payload, startTime, endTime });
  }

}
